import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ApiService } from 'src/app/_core/_services/api.service';

// One subscription's network view: the device's live status and signal
// (kept current by SmartOLT events / device refreshes) and every alarm on
// its circuit from the shared network alarm store - the same data as the
// Operations Network Alerts, for one customer.
@Component({
  selector: 'share-network-status',
  templateUrl: './share-network-status.component.html',
  styleUrls: ['./share-network-status.component.scss']
})
export class ShareNetworkStatusComponent implements OnChanges {
  @Input() subscriptionId?: number | null;
  @Input() heading = 'Network';
  // The circuit block: PPPoE, speeds, Wi-Fi (passwords only come back for
  // users allowed to change them).
  @Input() showCircuit = false;
  showPppoePassword = false;
  showWifiPassword = false;

  loading = false;
  error: string | null = null;
  view: any = null;
  macsLoading = false;
  macsError: string | null = null;

  constructor(private api: ApiService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['subscriptionId']) {
      this.load();
    }
  }

  load(): void {
    if (!this.subscriptionId) {
      this.view = null;
      return;
    }
    this.loading = true;
    this.error = null;
    this.api.getAPI(`portal/management/subscriptions/${this.subscriptionId}/network`).subscribe({
      next: (res: any) => {
        this.view = res?.body ?? null;
        this.loading = false;
      },
      error: () => {
        this.error = 'Could not load the network status.';
        this.loading = false;
      }
    });
  }

  // Reads the MACs behind the ONT from the OLT (SmartOLT or Tejas through
  // TECH8). Slow and one ONU at a time, so the result is stored on the
  // device and shown from there until read again.
  refreshMacs(): void {
    if (!this.subscriptionId || !this.view?.device) return;
    this.macsLoading = true;
    this.macsError = null;
    this.api
      .getAPI(`portal/management/subscriptions/${this.subscriptionId}/network/mac-addresses`, {
        refresh: true
      })
      .subscribe({
        next: (res: any) => {
          const body = res?.body ?? {};
          this.view = {
            ...this.view,
            device: {
              ...this.view.device,
              mac_addresses: body.mac_addresses || [],
              mac_addresses_fetched_at: body.fetched_at || new Date().toISOString()
            }
          };
          this.macsLoading = false;
        },
        error: (err: any) => {
          this.macsError = err?.error?.detail || 'The OLT did not answer. Try again in a minute.';
          this.macsLoading = false;
        }
      });
  }

  statusSeverity(status: string | null | undefined): string {
    switch ((status || '').toLowerCase()) {
      case 'online':
        return 'success';
      case 'los':
        return 'danger';
      case 'power fail':
        return 'warning';
      default:
        return 'secondary';
    }
  }

  // SmartOLT's bands for the OLT-side reading of the ONU (1310 nm).
  signalClass(dbm: number | null | undefined): string {
    if (dbm === null || dbm === undefined) return 'text-500';
    if (dbm >= -25) return 'text-green-600';
    if (dbm >= -28) return 'text-orange-600';
    return 'text-red-600';
  }

  sourceLabel(source: string): string {
    return source === 'smartolt' ? 'SmartOLT' : source === 'tejas' ? 'Tejas NMS' : source || '';
  }
}
